import Event from 'Event'
import {useAttendee, useEventAuth} from 'Event/auth'
import Login from 'Event/auth/Login'
import {useEvent} from 'Event/EventProvider'
import Step1 from 'Event/Step1'
import Step2 from 'Event/Step2'
import Step3 from 'Event/Step3'
import {createRoutes, routesWithValue} from 'lib/url'
import React, {useEffect} from 'react'
import {Redirect, Route, Switch, useRouteMatch} from 'react-router-dom'
import Speakers from 'Event/Speakers'
import PointsProvider from 'Event/PointsProvider'
import Leaderboard from 'Event/Leaderboard'
import Backgrounds from 'Event/Backgrounds'
import UnderConstruction from 'Event/UnderConstruction'
import TemplateProvider from 'Event/TemplateProvider'
import HTMLHead from 'Event/HTMLHead'
import ForgotPassword from 'Event/auth/ForgotPassword'
import ResetPassword from 'Event/auth/ResetPassword'
import FaqPage from 'Event/FAQs'
import SubmissionsProvider from 'Event/SubmissionsProvider'
import EventLanguageProvider, {
  AttendeeLanguageProvider,
  useLanguage,
} from 'Event/LanguageProvider'
import JoinArea from 'Event/JoinArea/JoinArea'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import DownloadReport from 'Event/DownloadReport'
import SelfCheckIn from 'Event/SelfCheckIn'
import AttendeeProfileProvider, {
  useAttendeeProfile,
} from 'Event/attendee-rules/AttendeeProfileProvider'
import {useTrackEventPage, useTrackOnLoad} from 'analytics'
import ChangePassword from 'Event/auth/ChangePassword/'
import RoomRegistration from 'Event/RoomRegistration'
import FormStageView from 'Event/FormStageView'
import TokenEmojiPage from 'Event/TokenEmojiPage'
import DownloadA360iReport from 'Event/DownloadA360iReport'
import EventOfflinePage from 'Event/EventOfflinePage'
import PagePoints, {LEADERBOARD} from 'Event/PointsProvider/PagePoints'
import CompletedOnboarding from 'Event/CompletedOnboarding'
import CustomRedirects from 'Event/Redirects/CustomRedirect'
import ObieRedirect from 'Event/Obie/ObieRedirect'
import ImageWaterfall from 'Event/ImageWaterfall'
import AutoLogout from 'auth/AutoLogout'
import AreaRegistration from 'Event/AreaRegistration'
import DateProvider from 'lib/date-time/DateProvider'
import {createGlobalStyle} from 'styled-components'
import BusinessMasteryRoute from 'Event/business-mastery/BusinessMasteryRoute'
import TeamCompetition from 'Event/business-mastery/TeamCompetition'
import CreateTeamPage from 'Event/business-mastery/CreateTeamPage'
import JoinTeamPage from 'Event/business-mastery/JoinTeamPage'
import DeepLinkProvider from 'DeepLinkProvider'
import Sponsors from 'Event/Sponsors'
import SignUp from 'Event/auth/SignUp'
import TeamJoinRoomPage from 'Event/TeamJoinRoomPage'
import {signJwt} from 'lib/crypto/sign-jwt'

export const eventRoutes = createRoutes({
  login: '/login',
  r: {
    ':redirect_slug': {},
  },
  ra: {
    ':redirect_slug': {},
  },
  obie: {
    ':attendee_link_slug': {},
  },
  forgotPassword: '/forgot_password',
  resetPassword: '/reset_password',
  changePassword: '/change_password',
  signUp: '/sign_up',
  step1: '/step_1',
  step2: '/step_2',
  step3: '/step_3',
  speakers: '/speakers',
  sponsors: '/sponsors',
  resources: '/resources',
  faq: '/faq',
  leaderboard: '/leaderboard',
  techCheck: '/tech_check',
  area: {
    registration: '/registration',
    ':area': {},
  },
  room: {
    registration: '/registration',
    ':room': {
      team_join: '/team_join',
    },
  },
  report: '/report',
  logout: '/logout',
  backgrounds: '/backgrounds',
  image_waterfall: '/image_waterfall',
  team_competition: {
    create: '/create',
    join: '/join',
  },
  purchase: '/purchase',
  certificates: '/certificates',
  form_moderation: {
    stage: {
      ':status': {},
    },
  },
  emoji_page: '/emoji_page',
})

/**
 * Event pages that a button/link may navigate to as a
 * relative link.
 */

export const EVENT_PAGES = {
  [eventRoutes.speakers]: 'Speakers',
  [eventRoutes.sponsors]: 'Sponsors',
  [eventRoutes.faq]: 'FAQ',
  [eventRoutes.leaderboard]: 'Leaderboard',
  [eventRoutes.backgrounds]: 'Backgrounds',
  [eventRoutes.report]: 'Download Report',
  [eventRoutes.image_waterfall]: 'Image Waterfall',
  [eventRoutes.certificates]: 'Certificates',
}

export type EventPages = typeof EVENT_PAGES

export const areaRoutes = (key: string) =>
  routesWithValue(':area', key, eventRoutes.area[':area'])

export default function Routes() {
  return (
    <>
      <GlobalStyles />
      <Content />
    </>
  )
}

function Content() {
  const {user, loading, logout} = useEventAuth()
  const {event} = useEvent()
  const isLogoutRoute = Boolean(useRouteMatch(eventRoutes.logout))

  useTrackEventPage({
    page: 'Accessed Site',
  })

  if (!event.template) {
    return <UnderConstruction />
  }

  if (!event.is_online) {
    return (
      <TemplateProvider template={event.template}>
        <EventOfflinePage />
      </TemplateProvider>
    )
  }

  if (loading) {
    return <FullPageLoader />
  }

  if (isLogoutRoute) {
    return <Logout logout={logout} />
  }

  if (user) {
    return (
      <AutoLogout user={user} logout={logout}>
        <TemplateProvider template={event.template}>
          <DeepLinkProvider>
            <Authenticated />
          </DeepLinkProvider>
        </TemplateProvider>
      </AutoLogout>
    )
  }

  return (
    <TemplateProvider template={event.template}>
      <HTMLHead>
        <EventLanguageProvider>
          <DeepLinkProvider shouldRememberLocation>
            <GuestRoutes />
          </DeepLinkProvider>
        </EventLanguageProvider>
      </HTMLHead>
    </TemplateProvider>
  )
}

function Logout({logout}: {logout: () => void}) {
  useEffect(() => {
    logout()
  }, [logout])

  return <Redirect to={eventRoutes.login} />
}

function Authenticated() {
  useTrackOnLoad({
    category: 'Attendee',
    action: 'Logged In',
  })
  const attendee = useAttendee()

  useSetUpwZendeskJwt()

  return (
    <PointsProvider>
      <SubmissionsProvider>
        <AttendeeProfileProvider groups={attendee.groups} tags={attendee.tags}>
          <DateProvider>
            <AttendeeLanguageProvider>
              <DynamicAttendeeProfileProvider>
                <HTMLHead>
                  <UserRoutes />
                </HTMLHead>
              </DynamicAttendeeProfileProvider>
            </AttendeeLanguageProvider>
          </DateProvider>
        </AttendeeProfileProvider>
      </SubmissionsProvider>
    </PointsProvider>
  )
}

/**
 * Provides dynamic groups/keys to the AttendeeProvider.
 *
 * @param props
 * @returns
 */
function DynamicAttendeeProfileProvider(props: {children: JSX.Element}) {
  const {current, defaultLanguage} = useLanguage()
  const {groups, tags} = useAttendeeProfile()

  const withDynamicGroups = {
    ...groups,
    // Append selected language as a group to allow rules
    // to apply
    __language__: current || defaultLanguage,
  }
  return (
    <AttendeeProfileProvider groups={withDynamicGroups} tags={tags}>
      {props.children}
    </AttendeeProfileProvider>
  )
}

function UserRoutes() {
  return (
    <Switch>
      <Route path={eventRoutes.r[':redirect_slug'].root}>
        <CustomRedirects />
      </Route>
      <Route path={eventRoutes.ra[':redirect_slug'].root}>
        <CustomRedirects />
      </Route>
      <Route path={eventRoutes.obie[':attendee_link_slug'].root}>
        <ObieRedirect />
      </Route>
      <Route path={eventRoutes.obie.root}>
        <ObieRedirect />
      </Route>
      <Route path={eventRoutes.step1}>
        <Step1 />
      </Route>
      <Route path={eventRoutes.step2}>
        <CompletedOnboarding step={1}>
          <Step2 />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.step3}>
        <CompletedOnboarding step={2}>
          <Step3 />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.root} exact>
        <CompletedOnboarding>
          <Event />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.speakers} exact>
        <CompletedOnboarding>
          <Speakers />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.sponsors} exact>
        <CompletedOnboarding>
          <Sponsors />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.faq} exact>
        <CompletedOnboarding>
          <FaqPage />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.leaderboard}>
        <CompletedOnboarding>
          <PagePoints page={LEADERBOARD}>
            <Leaderboard />
          </PagePoints>
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.area[':area'].root}>
        <JoinArea />
      </Route>
      <Route path={eventRoutes.room[':room'].team_join}>
        <TeamJoinRoomPage />
      </Route>
      <Route path={eventRoutes.report}>
        <CompletedOnboarding>
          <DownloadReport />
        </CompletedOnboarding>
      </Route>
      <Route path="/a360i/reports/:index">
        <DownloadA360iReport />
      </Route>
      <Route path={eventRoutes.backgrounds}>
        <CompletedOnboarding>
          <Backgrounds />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.techCheck}>
        <CompletedOnboarding step={2}>
          <SelfCheckIn />
        </CompletedOnboarding>
      </Route>
      <Route path={eventRoutes.changePassword}>
        <ChangePassword />
      </Route>
      <Route path={eventRoutes.image_waterfall}>
        <ImageWaterfall />
      </Route>
      <Route path={eventRoutes.team_competition.create}>
        <BusinessMasteryRoute>
          <CreateTeamPage />
        </BusinessMasteryRoute>
      </Route>
      <Route path={eventRoutes.team_competition.join}>
        <BusinessMasteryRoute>
          <JoinTeamPage />
        </BusinessMasteryRoute>
      </Route>
      <Route path={eventRoutes.team_competition.root}>
        <BusinessMasteryRoute>
          <TeamCompetition />
        </BusinessMasteryRoute>
      </Route>
      <Route path={eventRoutes.form_moderation.stage[':status'].root}>
        <FormStageView />
      </Route>
      <Route path={eventRoutes.emoji_page}>
        <TokenEmojiPage />
      </Route>
      <Redirect to={eventRoutes.root} />
    </Switch>
  )
}

function GuestRoutes() {
  return (
    <Switch>
      <Route path={eventRoutes.login}>
        <Login />
      </Route>
      <Route path={eventRoutes.r[':redirect_slug'].root}>
        <CustomRedirects />
      </Route>
      <Route path={eventRoutes.forgotPassword}>
        <ForgotPassword />
      </Route>
      <Route path={eventRoutes.resetPassword}>
        <ResetPassword />
      </Route>
      <Route path={eventRoutes.signUp}>
        <SignUp />
      </Route>
      <Route path={eventRoutes.room.registration}>
        <RoomRegistration />
      </Route>
      <Route path={eventRoutes.area.registration}>
        <AreaRegistration />
      </Route>
      <Route path={eventRoutes.form_moderation.stage[':status'].root}>
        <FormStageView />
      </Route>
      <Route path={eventRoutes.emoji_page}>
        <TokenEmojiPage />
      </Route>
      <Redirect to={eventRoutes.login} />
    </Switch>
  )
}

// TODO: Remove custom event code for 'upw0324' March 2024
function useSetUpwZendeskJwt() {
  const {
    event: {slug},
  } = useEvent()

  const {id, email, first_name, last_name} = useAttendee()

  useEffect(() => {
    if (slug !== 'upw0324') {
      return
    }

    // Generate a JWT per Zendesk docs to authenticate user
    // https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/enabling_auth_visitors/
    const payload = {
      external_id: `${id}`, // attendee id
      email_verified: true,
      email: email,
      name: `${first_name} ${last_name}`,
      scope: 'user', // only user is allowed
    }

    signJwt({
      payload,
      secret:
        'yMQroWjhS9nESw3nq5NLtgNuI0a3ZGxFD_QyPGNv48IUt2v9o8B-DAokwp5t3n6oVBSmjQLcIMlnj8bQjS21Dw',
      headers: {
        kid: 'app_65f95d3487e8a4e2154ef7ad',
      },
    })
      .then((jwt) => {
        // Messenger script needs to call login with JWT
        // https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/

        //@ts-ignore
        window.UPW_JWT = jwt
      })
      .catch(() => {
        // ignore errors
      })
  }, [slug, id, email, first_name, last_name])
}

const GlobalStyles = createGlobalStyle`
body {
  font-family: Arial, 'Times New Roman', Verdana;
}
`
